import { HttpErrorResponse } from "@angular/common/http";

/**
 * Post-hearing task surface (story 967). Rows are workflow worklist items filtered to post-hearing
 * work; assignment and completion run through the existing workflow runtime endpoints rather than a
 * separate local status field.
 */
export interface PostHearingTask {
  workflowInstanceId: number;
  workflowETag?: string;
  stepETag?: string;
  subjectTypeCode: string;
  subjectId: number;
  workflowStatusCode: string;
  currentStepCode?: string | null;
  currentStepStatusCode?: string | null;
  assignmentPostureCode?: string | null;
  currentQueueDisplayText?: string | null;
  currentAssigneeDisplayName?: string | null;
  statusChangedUtc: string;
  startedUtc?: string | null;
  completedUtc?: string | null;
}

export interface PostHearingTaskPage {
  totalCount: number;
  items: PostHearingTask[];
}

export interface PostHearingCompletionSource {
  workflowInstanceId: number;
  workflowStepInstanceId: number | null;
  stepCode: string | null;
  eTag: string;
}

export interface WorkflowDecisionRecord {
  id: number;
  workflowInstanceId?: number | null;
  workflowStepInstanceId?: number | null;
  decisionRecordTypeCode: string;
  decisionPurposeCode?: string | null;
  statusCode: string;
  notes?: string | null;
  requestedByUserId?: number | null;
  decidedByUserId?: number | null;
  createdUtc: string;
  decidedUtc?: string | null;
}

export interface WorkflowDecisionRecordPage {
  totalCount: number;
  items: WorkflowDecisionRecord[];
}

export interface PostHearingTaskFilters {
  status: string;
}

export const EMPTY_POST_HEARING_FILTERS: PostHearingTaskFilters = { status: "" };

/** Post-hearing workflow queue code (PhmsWorkflowSeedData.PostHearingQueueCode). */
export const POST_HEARING_QUEUE_CODE = "post-hearing";

const WORKFLOW_COMPLETED = "completed";
const STEP_IN_PROGRESS = "in_progress";

export interface PostHearingTaskDisplayError {
  title: string;
  messages: string[];
  statusCode?: number;
}

/** A completed workflow exposes no further completion action. */
export function isWorkflowCompleted(task: PostHearingTask): boolean {
  return task.workflowStatusCode === WORKFLOW_COMPLETED;
}

/** A required step can be completed only while it is in progress. */
export function canCompleteStep(task: PostHearingTask): boolean {
  return !isWorkflowCompleted(task)
    && typeof task.currentStepCode === "string"
    && task.currentStepCode.trim().length > 0
    && task.currentStepStatusCode === STEP_IN_PROGRESS;
}

export function formatWorkflowStatus(statusCode: string | null | undefined): string {
  return humanize(statusCode, "Unknown");
}

export function formatAssignmentPosture(code: string | null | undefined): string {
  return humanize(code, "—");
}

export function formatSubject(task: PostHearingTask): string {
  return task.subjectId > 0 ? `IP #${task.subjectId}` : "—";
}

export function mapPostHearingError(error: unknown): PostHearingTaskDisplayError {
  if (error instanceof HttpErrorResponse) {
    const statusCode = error.status;
    if (statusCode === 401) {
      return { title: "Sign-in required", messages: ["Your session is no longer authenticated."], statusCode };
    }
    if (statusCode === 403) {
      return { title: "Access denied", messages: ["You do not have permission to act on this task."], statusCode };
    }
    if (statusCode === 404) {
      return { title: "Task not found", messages: ["The task could not be found. It may have already moved on."], statusCode };
    }
    if (statusCode === 409) {
      return { title: "Task already changed", messages: ["This task changed since it was loaded. Refresh and try again."], statusCode };
    }
    if (statusCode === 400) {
      return { title: "Invalid filter", messages: ["The request used an unsupported filter."], statusCode };
    }
    return { title: "Post-hearing tasks unavailable", messages: ["The post-hearing tasks could not be loaded."], statusCode };
  }
  return { title: "Unexpected error", messages: ["The post-hearing tasks could not be loaded."] };
}

function humanize(value: string | null | undefined, fallback: string): string {
  const normalized = (value ?? "").trim();
  if (!normalized) {
    return fallback;
  }
  return normalized
    .split(/[._\-\s]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}
